import { useCallback, useMemo, useState } from "react";
import {
  BaggageEventTypes,
  BaggageSelectors,
  FlightSelectors,
  useCheckoutSend,
} from "@checkout/index";
import { useCheckoutStateSelector as useSelector } from "@hopper-b2b/checkout";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import { ButtonWrap, MobilePopoverCard } from "@hopper-b2b/ui";
import CarryOnIcon from "../../../../assets/client/baggage-carry-on.svg?react";
import CheckedBagIcon from "../../../../assets/client/baggage-checked-bag.svg?react";
import PersonalItemIcon from "../../../../assets/client/baggage-personal-item.svg?react";
import Chevron from "../../../../assets/client/chevron.svg?react";
import Close from "../../../../assets/client/close.svg?react";
import { CheckoutHeader, CountPickerRow } from "../../../../components/";
import { Event, type TEvent } from "../../events";
import styles from "./styles.module.scss";

export const BaggageSelection = () => {
  const send = useCheckoutSend<TEvent>();
  const { t, formatFiatCurrency } = useI18nContext();
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);

  // Passengers
  const passengers = useSelector(
    FlightSelectors.mergePassengersAndBreakdownPersons
  );

  // Allowances
  const carryOnAllowance = useSelector(BaggageSelectors.getCarryOnAllowance);
  const checkedBaggageAllowance = useSelector(
    BaggageSelectors.getCheckedBaggageAllowance
  );
  const personalItemAllowance = useSelector(
    BaggageSelectors.getPersonalItemAllowance
  );

  const passengerBagPriceMap = useSelector(
    BaggageSelectors.getPassengerBagPriceMap
  );

  // Get existing bag selections to pre-populate the UI
  const passengerBagsMap = useSelector(
    BaggageSelectors.getPassengerExtraBaggageMap
  );

  // Checkout only supports adding checked baggage
  const checkedBagPrices = useSelector(BaggageSelectors.getCheckedBagsPrices);

  // Baggage total cost
  const totalCombinedCost = useSelector(
    BaggageSelectors.extraBaggageCostTotalCombined
  );

  const onBack = useCallback(() => {
    send({ type: BaggageEventTypes.PREVIOUS });
  }, [send]);

  const showInfoHandler = useCallback(() => {
    setShowAdditionalInfo(!showAdditionalInfo);
  }, [showAdditionalInfo]);

  const continueWithoutBags = useCallback(() => {
    send(Event.NEXT);
  }, [send]);

  const continueWithBags = useCallback(() => {
    send(Event.NEXT);
  }, [send]);

  const totalBaggageCount = useSelector(
    BaggageSelectors.extraBaggageCountTotal
  );

  const addBaggageHandler = useCallback(
    (count: number, { passengerId }: { passengerId: string }) => {
      send({
        type: BaggageEventTypes.SELECT_BAGGAGE,
        passengerId: passengerId,
        numCheckedBags: count,
      });
    },
    [send]
  );

  const listedAllowances = useMemo(() => {
    let allowances = [];
    if (carryOnAllowance > 0) {
      allowances.push(
        <div className="allowance-item">
          <CarryOnIcon className="allowance-icon" />
          {t("baggage.carryOnAllowance", { count: carryOnAllowance })}
        </div>
      );
    }
    if (personalItemAllowance > 0) {
      allowances.push(
        <div className="allowance-item">
          <PersonalItemIcon className="allowance-icon" />
          {t("baggage.personalItemAllowance", {
            count: personalItemAllowance,
          })}
        </div>
      );
    }
    if (checkedBaggageAllowance > 0) {
      allowances.push(
        <div className="allowance-item">
          <CheckedBagIcon className="allowance-icon" />
          {t("baggage.checkedBaggageAllowance", {
            count: checkedBaggageAllowance,
          })}
        </div>
      );
    }
    return allowances;
  }, [carryOnAllowance, checkedBaggageAllowance, personalItemAllowance, t]);

  return (
    <>
      <CheckoutHeader onBack={onBack} />
      <div className={styles.BaggageSelection}>
        <h2 className="bags-title">{t("baggage.addBagsTitle")}</h2>
        {listedAllowances.length > 0 ? (
          <>
            <p className="bags-description">
              {t("baggage.ticketIncludesBaggage")}
            </p>
            <ButtonWrap
              className="baggage-information-row"
              onClick={showInfoHandler}
            >
              <p>{t("baggage.information")}</p>
              <Chevron className="icon" />
            </ButtonWrap>
            {listedAllowances.map((listedAllowance) => listedAllowance)}
          </>
        ) : null}
        <p className="bags-description fee">{t("baggage.addExtraForFee")}</p>
        <div className="selection-section">
          {passengers.map((passenger) => {
            return (
              <div
                className="passenger-section"
                key={`checked-bag-${passenger.id}`}
              >
                <p className="passenger-name">
                  {passenger.givenName} {passenger.surname}
                </p>
                <CountPickerRow
                  title={t("baggage.checkedBag")}
                  subtitle={
                    passengerBagPriceMap.get(passenger.id)?.value
                      ? t("baggage.priceTotal", {
                          price: formatFiatCurrency({
                            ...passengerBagPriceMap.get(passenger.id),
                          }),
                        })
                      : t("baggage.fromPrice", {
                          price: formatFiatCurrency(checkedBagPrices[0]),
                        })
                  }
                  baseCount={
                    passengerBagsMap?.get(passenger.id)?.checkedBags ?? 0
                  }
                  maxCount={checkedBagPrices.length ?? 0}
                  updateCount={addBaggageHandler}
                  additionalContext={{ passengerId: passenger.id }}
                />
              </div>
            );
          })}
        </div>
        <div className="bottom-action-section">
          {totalBaggageCount > 0 ? (
            <>
              <div className="left">
                <p className="subtitle">{t("baggage.extraBaggage")}</p>
                <p className="total">
                  {t("baggage.totalPrice", {
                    price: formatFiatCurrency({
                      value: totalCombinedCost.total,
                      currencyCode: totalCombinedCost.currencyCode,
                    }),
                  })}
                </p>
              </div>
              <button className="continue" onClick={continueWithBags}>
                {t("continue")}
              </button>
            </>
          ) : (
            <button
              onClick={continueWithoutBags}
              className="continue-without-selection"
            >
              {t("baggage.continueWithoutBags")}
            </button>
          )}
        </div>
      </div>

      <MobilePopoverCard
        open={showAdditionalInfo}
        className={styles.AdditionalInfoPopover}
        onClose={showInfoHandler}
        centered={false}
        contentClassName="baggage-info-popover-content"
      >
        <>
          <ButtonWrap onClick={showInfoHandler}>
            <Close className="close" />
          </ButtonWrap>
          <h2 className="baggage-info-title">{t("baggage.information")}</h2>
          <p className="baggage-info-content">{t("baggage.informationOne")}</p>

          <p className="baggage-info-content">
            <Trans i18nKey="baggage.informationTwo" components={[<b />]} />
          </p>
          <p className="baggage-info-content">
            <Trans i18nKey="baggage.informationThree" components={[<b />]} />
          </p>
          <p className="baggage-info-content last">
            <Trans i18nKey="baggage.informationFour" components={[<b />]} />
          </p>
          <button className="close-button" onClick={showInfoHandler}>
            {t("close.button")}
          </button>
        </>
      </MobilePopoverCard>
    </>
  );
};
